import { Box, Button, Grid, SvgIcon, Typography } from '@mui/material';
import { PosMappingsProvider, usePosMappingsContext } from '../context/PosMappingsContext';
import { PosSkuMapping } from './tabs/PosSkuMapping';
import { PosSkuTable } from '../components/tabs/PosSkuTable';
import { Plus } from '../../../assets';
import PageTitle from '../../../components/shared/PageTitle';
import POSMappingDeleteModal from './POSMappingDeleteModal';
import ItemMappingModal from './ItemMappingModal';
import { GetPosMappingResponse } from '../../../app/services/api/generated';
import CustomPagination from '../../../components/forms/Pagination';
import { useTranslation } from 'react-i18next';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import React, { useState } from 'react';
import LoadingWheel from '../../../components/ui/LoadingWheel';
import { MappingType } from '../enums/MappingType';
import { Section } from '../../../components/ui/Section';
import PosMappingsBulkManage from './PosMappingsBulkManage';

function PosMappingsInner() {
  const {
    onChange, handleDelete, showDeleteModal, setShowDeleteModal, selectedEntity, showEditModal,
    closeEditModal, showCreateModal, openCreateModal, closeCreateModal, totalCount, page, setPage, maxPages, showResultMessage, setShowResultMessage, searchPOSMapping, isLoading,
  } = usePosMappingsContext();
  const { t } = useTranslation('posMapping');
  const [showBulkManage, setShowBulkManage] = useState(false);

  const convertEntityToFormData = (entity: GetPosMappingResponse | undefined) => {
    if (!entity) return undefined;

    const posEntity = entity as GetPosMappingResponse;
    return {
      itemNumber: posEntity.target || '',
      mappingType: MappingType.POS,
      itemType: posEntity.mappingType?.toString() || '',
      posItemNumber: posEntity.source || '',
      franchise: posEntity.franchiseNames || [],
      allStores: posEntity.stores?.length === 0,
      stores: posEntity.stores ? posEntity.stores.map(s => s.storeName || s.storeNumber || '') : [],
      originalPosItemNumber: posEntity.source || '',
    };
  };

  const handleEditSave = () => {
    closeEditModal();
    searchPOSMapping();
  };

  const handleCloseCreateModal = () => {
    closeCreateModal();
    searchPOSMapping();
  };

  const handleSaveCreateModal = () => {
    closeCreateModal();
    searchPOSMapping();
  };

  const handleCloseEditModal = () => {
    closeEditModal();
    searchPOSMapping();
  };

  const handleDeleteCancel = () => setShowDeleteModal(false);

  const handleDeleteConfirm = () => {
    handleDelete();
    setShowDeleteModal(false);
  };

  return (
    <Grid container>
      <Grid item
        xs={12}
        md={9}
        sx={{ textAlign: 'left' }}>
        <PageTitle>{t('itemMapping')}</PageTitle>
        <Typography variant="textMD"
          sx={{ my: 3 }}>
          {t('itemMappingDescription')}
        </Typography>
      </Grid>
      <Grid item
        xs={12}
        md={3}>
        <Box sx={{ display: 'flex', alignItems: 'top', justifyContent: 'flex-end' }}>
          <Button
            variant='primary'
            size='lg'
            sx={{ ml: 4 }}
            onClick={openCreateModal}
            startIcon={<SvgIcon><Plus fill='white' /></SvgIcon>}>{t('createNew')}
          </Button>
        </Box>
      </Grid>
      <Grid item
        xs={12}>
        <Box sx={{ mt: 2 }}>
          {isLoading ? (
            <LoadingWheel />
          ) : (
            <>
              <PosSkuMapping onOpenBulkManage={() => setShowBulkManage(true)} />

              <Section>
                <PosSkuTable/>
                {totalCount > 0 && (
                  <CustomPagination
                    page={page}
                    setPage={setPage}
                    maxPages={maxPages}
                    breakpointForChangeDisplay={120000}
                  />
                )}
              </Section>
            </>
          )}
        </Box>
      </Grid>
      <ItemMappingModal open={showCreateModal}
        onClose={handleCloseCreateModal}
        onSave={handleSaveCreateModal}
        setShowResultMessage={setShowResultMessage}
        onTabSwitch={(tabIndex) => onChange(null, tabIndex)} />
      <ItemMappingModal
        open={showEditModal}
        onClose={handleCloseEditModal}
        onSave={handleEditSave}
        editMode={true}
        initialData={convertEntityToFormData(selectedEntity)}
        setShowResultMessage={setShowResultMessage}
      />
      <POSMappingDeleteModal
        open={showDeleteModal}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
      <MessagesSnackbar
        open={showResultMessage.open}
        onClose={() => setShowResultMessage({ ...showResultMessage, open: false })}
        message={showResultMessage.message}
        severity={showResultMessage.severity}
        duration={showResultMessage.severity === 'error' ? null : 6000}
      />
      <PosMappingsBulkManage
        open={showBulkManage}
        handleClose={() => setShowBulkManage(false)}
        onSuccess={(message) => {
          setShowBulkManage(false);
          setShowResultMessage({ open: true, message, severity: 'success' });
          searchPOSMapping();
        }}
      />
    </Grid>
  );
}

export default function PosMappingsPage() {
  return (
    <PosMappingsProvider>
      <PosMappingsInner />
    </PosMappingsProvider>
  );
}
